import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Table } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import { fetchCompnayCodesById } from "../Api/InvoiceApi";
import { getStores } from "../Api/PurchaseOrder";

const LOCAL_STORAGE_KEY = "inventory_stock_items_v1";

export default function InventoryStockIn() {
  const { user } = useAuth();

  const [companyCode, setCompanyCode] = useState("");
  const [storeId, setStoreId] = useState("");

  const [itemCode, setItemCode] = useState("");
  const [quantity, setQuantity] = useState("");
  const [stockPrice, setStockPrice] = useState("");
  const [salesPrice, setSalesPrice] = useState("");
  const [itemName, setItemName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stores, setStores] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  const [items, setItems] = useState([]);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // if you want scanner Enter to auto-add, keep true; otherwise set false
  const autoAddOnScan = true;

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const orgId = user?.organisationId;
        const [companyResp, storesResp] = await Promise.all([fetchCompnayCodesById(orgId), getStores()]);

        let companies = [];
        if (Array.isArray(companyResp)) companies = companyResp;
        else if (companyResp && Array.isArray(companyResp.data)) companies = companyResp.data;
        else if (companyResp && Array.isArray(companyResp.result)) companies = companyResp.result;
        setCompanyCodes(companies || []);

        let rawStores = [];
        if (Array.isArray(storesResp)) rawStores = storesResp;
        else if (storesResp && Array.isArray(storesResp.data)) rawStores = storesResp.data;
        else if (storesResp && Array.isArray(storesResp.stores)) rawStores = storesResp.stores;
        else if (storesResp && typeof storesResp === "object") {
          const firstArray = Object.values(storesResp).find(Array.isArray);
          if (Array.isArray(firstArray)) rawStores = firstArray;
        }
        setAllStores(rawStores || []);

        const defaultCompany =
          companies && companies.length > 0 ? companies[0].companyCode ?? companies[0].code ?? companies[0].value ?? companies[0] : "";
        if (defaultCompany) {
          setCompanyCode(defaultCompany);
          await fetchStoresForCompany(defaultCompany, rawStores);
        }
      } catch (err) {
        console.error("Error loading dropdowns:", err);
        toast.error("Error loading company/store dropdowns");
        setCompanyCodes([]);
        setAllStores([]);
        setStores([]);
      }
    };

    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterAllStoresByCompany = (companyCodeParam, rawStores = allStores) => {
    if (!companyCodeParam) return [];
    const codeLower = String(companyCodeParam).toLowerCase();
    return (rawStores || []).filter((s) => {
      const storeCompany = s.companyCode ?? s.company ?? s.company?.companyCode ?? s.company?.code ?? s.companyCode;
      if (!storeCompany) return false;
      return String(storeCompany).toLowerCase() === codeLower;
    });
  };

  const fetchStoresForCompany = async (companyCodeParam, rawStoresFallback) => {
    if (!companyCodeParam) {
      setStores([]);
      setStoreId("");
      return;
    }

    try {
      const res = await axios.get(`${Base_URL}/StoreMaster/id?id=${encodeURIComponent(companyCodeParam)}`);
      let payload = res.data;
      let list = [];

      if (Array.isArray(payload)) list = payload;
      else if (payload && typeof payload === "object") {
        if (Array.isArray(payload.data)) list = payload.data;
        else if (Array.isArray(payload.stores)) list = payload.stores;
        else if (Array.isArray(payload.result)) list = payload.result;
        else {
          const firstArray = Object.values(payload).find(Array.isArray);
          if (Array.isArray(firstArray)) list = firstArray;
        }
      }

      if ((!list || list.length === 0) && (rawStoresFallback || allStores)) {
        list = filterAllStoresByCompany(companyCodeParam, rawStoresFallback || allStores);
      }

      setStores(list || []);
      if (list && list.length > 0) {
        const firstStore = list[0];
        const idValue = firstStore.storeId ?? firstStore.id ?? firstStore.code ?? firstStore.value;
        setStoreId(idValue);
      } else {
        setStoreId("");
      }
    } catch (err) {
      console.error("Error fetching stores for company:", err);
      const fallbackList = filterAllStoresByCompany(companyCodeParam);
      setStores(fallbackList || []);
      if (fallbackList && fallbackList.length > 0) {
        const firstStore = fallbackList[0];
        const idValue = firstStore.storeId ?? firstStore.id ?? firstStore.code ?? firstStore.value;
        setStoreId(idValue);
      } else {
        setStoreId("");
      }
      toast.error("Error loading stores for selected company (using fallback)");
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch (e) {
      console.warn("Could not parse stored items", e);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn("Failed to save items to localStorage", e);
    }
  }, [items]);

  const isDuplicate = (code) => {
    if (!code) return false;
    return items.some((it) => String(it.itemCode).toLowerCase() === String(code).toLowerCase());
  };

  const addNewItemObject = ({ code, name, qty, stock, sales }) => {
    const trimmed = code?.trim();
    if (!trimmed) return;
    if (isDuplicate(trimmed)) return;
    const newItem = {
      itemCode: trimmed,
      itemName: name || null,
      quantity: Number(qty) || 1,
      stockPrice: stock === "" || stock == null ? null : Number(stock),
      salesPrice: sales === "" || sales == null ? null : Number(sales),
    };
    setItems((prev) => [...prev, newItem]);
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!itemCode || itemCode.trim() === "") {
      setItemName("");
      setSalesPrice("");
      setError("");
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return;
    }

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const fetchItem = async () => {
        setLoading(true);
        setError("");
        setItemName("");
        setSalesPrice("");

        try {
          const url = `${Base_URL}/Item/GetItemById/id?id=${encodeURIComponent(itemCode.trim())}`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();

          const nameFromApi = data?.itemName || "";
          const apiSalesPrice = data?.salesPrice ?? data?.price ?? data?.salePrice ?? data?.mrp ?? null;

          setItemName(nameFromApi);
          setQuantity(1);
          setSalesPrice(apiSalesPrice !== null && apiSalesPrice !== undefined ? String(apiSalesPrice) : "");
          // NOTE: automatic add removed — user must click + Add Item or press Enter (scanner) to add.
        } catch (err) {
          if (err.name !== "AbortError") setError(err.message || "Failed to fetch item");
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCode]);

  const handleAddItem = () => {
    setError("");
    if (!itemCode.trim()) {
      setError("Item code is required to add an item.");
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      setError("Quantity should be a positive number.");
      return;
    }
    if (isDuplicate(itemCode.trim())) {
      setError("This item code is already added.");
      return;
    }

    const newItem = {
      itemCode: itemCode.trim(),
      itemName: itemName || null,
      quantity: Number(quantity),
      stockPrice: stockPrice === "" ? null : Number(stockPrice),
      salesPrice: salesPrice === "" ? null : Number(salesPrice),
    };

    setItems((prev) => [...prev, newItem]);

    setItemCode("");
    setItemName("");
    setQuantity("");
    setStockPrice("");
    setSalesPrice("");
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAll = async () => {
    if (!companyCode) {
      toast.error("Please select Company Code before saving.");
      return;
    }

    const payload = {
      createdBy: user?.id ?? user?.userId ?? user?.username ?? null,
      createdAt: new Date().toISOString(),
      organisationId: user?.organisationId,
      companyCode: companyCode,
      storeId: storeId || null,
      items,
    };

    try {
      const res = await axios.post(`${Base_URL}/Inventory/InventoryStockIn`, payload);
      if (res.status === 200) {
        toast.success("Items Updated in inventory");
        setItems([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      } else {
        toast.error("Error adding items in inventory");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || err.message || "Failed to save items");
    }
  };

  const onItemCodeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (autoAddOnScan) {
        if (quantity && Number(quantity) > 0) {
          if (!isDuplicate(itemCode.trim())) {
            handleAddItem();
          } else {
            setError("This item code is already added.");
            setItemCode("");
          }
        } else {
          setError("Please enter quantity (scanner didn't provide it).");
        }
      }
    }
  };

  return (
    <Form className="p-4 bg-white rounded shadow-sm" onSubmit={(e) => e.preventDefault()}>
      <h4 className="mb-4 fw-bold">Inventory Stock In</h4>

      <Row>
        <Col md={3} className="mb-3">
          <Form.Group controlId="companyCode">
            <Form.Label>Company Code</Form.Label>
            <Form.Select
              value={companyCode}
              onChange={(e) => {
                const v = e.target.value;
                setCompanyCode(v);
                fetchStoresForCompany(v);
              }}
            >
              <option value="">Select company</option>
              {companyCodes.map((cc) => (
                <option key={cc.companyCode ?? cc.code ?? cc} value={cc.companyCode ?? cc.code ?? cc}>
                  {cc.companyCode ?? cc.code ?? cc}-{cc.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3} className="mb-3">
          <Form.Group controlId="storeId">
            <Form.Label>Store</Form.Label>
            <Form.Select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              <option value="">Select store</option>
              {stores.map((s, i) => (
                <option key={s.id ?? s.storeId ?? i} value={s.id ?? s.storeId ?? s.code ?? i}>
                  {s.name ?? s.storeName ?? s.code ?? `Store ${i + 1}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} />
      </Row>

      <Row className="mt-2">
        <Col md={3} className="mb-3">
          <Form.Group controlId="itemCode">
            <Form.Label>Item Code</Form.Label>
            <Form.Control
              id="itemCodeInput"
              type="text"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
              onKeyDown={onItemCodeKeyDown}
              placeholder="Enter or scan item code"
              autoComplete="off"
              autoFocus
            />
          </Form.Group>
        </Col>

        <Col md={3} className="mb-3">
          <Form.Group controlId="itemName">
            <Form.Label>Item Name</Form.Label>
            <Form.Control
              type="text"
              value={loading ? "Loading..." : itemName}
              disabled
              placeholder="Item name will appear here"
              className="bg-light"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="mb-3">
          <Form.Group controlId="quantity">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Qty"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="mb-3">
          <Form.Group controlId="stockPrice">
            <Form.Label>Stock Price</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={stockPrice}
              onChange={(e) => setStockPrice(e.target.value)}
              placeholder="Stock price"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="mb-3">
          <Form.Group controlId="salesPrice">
            <Form.Label>Sales Price</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={salesPrice}
              onChange={(e) => setSalesPrice(e.target.value)}
              placeholder="Sales price (from API)"
            />
          </Form.Group>
        </Col>
      </Row>

      {error && (
        <Row>
          <Col>
            <small className="text-danger">{error}</small>
          </Col>
        </Row>
      )}

      <Row className="mt-2">
        <Col>
          <button type="button" className="btn btn-outline-primary me-2" onClick={handleAddItem}>
            + Add Item
          </button>

          <button type="button" className="btn btn-primary" onClick={handleSaveAll} disabled={items.length === 0}>
            Save All ({items.length})
          </button>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          {items.length === 0 ? (
            <div className="text-muted">No items added yet.</div>
          ) : (
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Qty</th>
                  <th>Stock Price</th>
                  <th>Sales Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{it.itemCode}</td>
                    <td>{it.itemName ?? "-"}</td>
                    <td>{it.quantity}</td>
                    <td>{it.stockPrice ?? "-"}</td>
                    <td>{it.salesPrice ?? "-"}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={() => handleRemoveItem(idx)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </Form>
  );
}
